import { useEffect, useMemo, useRef, useState } from 'react';
import {
  grimdark_hud_1,
  grimdark_hud_cables,
  hud,
  screen,
} from '../../../images/index.js';
import CampaignSelectionMenu from './CampaignSelectionMenu.js';
import { MACHINE_RITES } from '../../../data/MACHINE_RITES.js';

import {
  makePickLeft,
  makePickRight,
  createHudTextController,
  createNoiseController,
} from './grimdarkHudUtils.js';

const RAISE_MS = 3120;
const MAX_LEFT_LINES = 3;
const MAX_RIGHT_LINES = 44;

export default function GrimdarkHud({ hudRaised }) {
  const noiseCanvasRef = useRef(null);

  const [leftLines, setLeftLines] = useState([]);
  const [rightLines, setRightLines] = useState([]);

  const pickLeft = useMemo(() => makePickLeft(MACHINE_RITES), []);
  const pickRight = useMemo(() => makePickRight(), []);

  const textCtlRef = useRef(null);
  const noiseCtlRef = useRef(null);
  const leftLastIndex = leftLines.length - 1;

  // init controllers once
  useEffect(() => {
    textCtlRef.current = createHudTextController({
      setLeftLines,
      setRightLines,
      pickLeft,
      pickRight,
      maxLeftLines: MAX_LEFT_LINES,
      maxRightLines: MAX_RIGHT_LINES,
      rightIntervalMs: 55,
    });

    noiseCtlRef.current = createNoiseController({
      canvasRef: noiseCanvasRef,
      raiseMs: RAISE_MS,
      frameDelayMs: 120,
      tickMs: 70,
    });

    return () => {
      textCtlRef.current?.stop?.();
      noiseCtlRef.current?.stop?.();
    };
  }, [pickLeft, pickRight]);

  // start/stop text based on raise
  useEffect(() => {
    textCtlRef.current?.stop?.();
    if (!hudRaised) return;
    textCtlRef.current?.startAfter?.(RAISE_MS + 420);
    return () => textCtlRef.current?.stop?.();
  }, [hudRaised]);

  // DPR resize handler
  useEffect(() => {
    const noiseCtl = noiseCtlRef.current;
    if (!noiseCtl) return;

    noiseCtl.resizeToDpr();
    window.addEventListener('resize', noiseCtl.resizeToDpr);
    return () => window.removeEventListener('resize', noiseCtl.resizeToDpr);
  }, []);

  // start/stop noise based on raise
  useEffect(() => {
    const noiseCtl = noiseCtlRef.current;
    if (!noiseCtl) return;

    noiseCtl.stop();
    if (hudRaised) noiseCtl.startAfterRaise();
    return () => noiseCtl.stop();
  }, [hudRaised]);

  return (
    <div className={`grimdark-rig ${hudRaised ? 'grimdark-rig--raise' : ''}`}>
      <div className={`grimdark-rig__screenWrap ${hudRaised ? 'is-on' : ''}`}>
        <img
          src={screen}
          alt="screen"
          className={`grimdark-rig__screen ${
            hudRaised ? 'screen--powerOn' : ''
          }`}
          draggable={false}
        />

        <canvas ref={noiseCanvasRef} className="grimdarkNoiseCanvas" />
        <div className="grimdarkScreenPerimeter" />

        <div className={`grimdarkTerminal ${hudRaised ? 'is-on' : ''}`}>
          <div className="grimdarkTerminal__left">
            {leftLines.map((line, i) => {
              const isLast = i === leftLastIndex;
              return (
                <div
                  key={i}
                  className="grimdarkTerminal__line grimdarkTerminal__line--big"
                >
                  {line}
                  {isLast && <span className="grimdarkCursor">▮</span>}
                </div>
              );
            })}
          </div>

          <div className="grimdarkTerminal__right">
            {rightLines.map((line, i) => (
              <div
                key={i}
                className="grimdarkTerminal__line grimdarkTerminal__line--small"
              >
                {line}
              </div>
            ))}
          </div>
        </div>

        <CampaignSelectionMenu isOn={hudRaised} />
      </div>

      <img
        src={grimdark_hud_cables}
        alt=""
        className="grimdark-rig__cables"
        draggable={false}
      />
      <img
        src={grimdark_hud_1}
        alt="hud"
        className="grimdark-rig__frame"
        draggable={false}
      />
    </div>
  );
}
